import { LitElement, css, html, nothing } from 'lit';
import { sharedStyles } from '../styles/shared.js';
import './qz-button.js';

/** Od tylu sekund do wygaśnięcia odliczanie robi się czerwone. */
const SOON_S = 20;

/**
 * Karta zaproszenia do pojedynku, przyklejona do dołu okna.
 *
 * Zaproszenie pojawia się jako karta w rogu, nie jako okno modalne: nie ma
 * powodu przerywać komuś tego, co właśnie robi, a modal zamykany odruchowo
 * kosztowałby zaproszenie.
 *
 * Komponent jest czysty — nie odpytuje API i nie zna zegara. Pozostały czas
 * dostaje właściwością, a decyzję oddaje zdarzeniem. Nasłuchem zajmuje się
 * `qz-invite-watch`.
 *
 * @prop {{ person: { name: string }, categoryName?: string } | null} invite
 * @prop {number} secondsLeft — ile sekund zostało do wygaśnięcia
 * @prop {boolean} busy — dołączanie w toku
 *
 * @fires qz-accept — kliknięto „Dołącz"
 * @fires qz-decline — kliknięto „Odrzuć"
 */
export class QzInviteCard extends LitElement {
  static properties = {
    invite: { type: Object },
    secondsLeft: { type: Number },
    busy: { type: Boolean },
  };

  static styles = [
    sharedStyles,
    css`
      :host {
        position: fixed;
        right: var(--qz-space-4);
        /* Na telefonie nawigacja siedzi na dole ekranu — karta zaproszenia
           wchodzi nad nią, nie pod nią. Zapas podaje powłoka aplikacji. */
        bottom: calc(var(--qz-space-4) + var(--qz-bottom-inset, 0px));
        z-index: 90;
        max-width: min(22rem, calc(100vw - 2 * var(--qz-space-4)));
      }

      .invite {
        padding: var(--qz-space-4) var(--qz-space-6);
        background: var(--qz-surface);
        border: 1px solid var(--qz-accent);
        border-radius: var(--qz-radius-lg);
        box-shadow: var(--qz-shadow);
        animation: rise 200ms cubic-bezier(0.2, 0, 0.2, 1);
      }

      @keyframes rise {
        from {
          opacity: 0;
          transform: translateY(0.5rem);
        }
      }

      p {
        margin: var(--qz-space-2) 0 var(--qz-space-4);
        line-height: 1.5;
      }

      strong {
        font-weight: 600;
      }

      .actions {
        display: flex;
        gap: var(--qz-space-2);
      }

      /* Odliczanie do wygaśnięcia — zaproszenie żyje dwie minuty i widać to. */
      .left {
        font-family: var(--qz-font-mono);
        font-size: var(--qz-text-xs);
        color: var(--qz-ink-muted);
        font-variant-numeric: tabular-nums;
        transition: color var(--qz-motion-enter) var(--qz-ease);
      }

      /*
       * Ostatnie dwadzieścia sekund: odliczanie zmienia kolor przejściem.
       * Bez pulsowania i bez powiększania — zaproszenie ma zwrócić uwagę,
       * a nie zabrać jej pytaniu, które gracz może akurat czytać.
       */
      .left[data-soon] {
        color: var(--qz-danger);
      }
    `,
  ];

  constructor() {
    super();
    this.invite = null;
    this.secondsLeft = 0;
    this.busy = false;
  }

  render() {
    const invite = this.invite;
    if (!invite) return nothing;

    const seconds = Math.max(0, this.secondsLeft);

    return html`
      <div class="invite" role="alert">
        <span class="label">zaproszenie</span>
        <p>
          <strong>${invite.person.name}</strong> chce z Tobą
          zagrać${invite.categoryName ? html` — <em>${invite.categoryName}</em>` : nothing}.
        </p>
        <div class="actions">
          <qz-button size="sm" ?busy=${this.busy} @click=${() => this.#emit('qz-accept')}>
            Dołącz
          </qz-button>
          <qz-button size="sm" variant="ghost" @click=${() => this.#emit('qz-decline')}>
            Odrzuć
          </qz-button>
          <span class="left" ?data-soon=${seconds <= SOON_S}>${seconds} s</span>
        </div>
      </div>
    `;
  }

  #emit(type) {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true }));
  }
}

customElements.define('qz-invite-card', QzInviteCard);
